import { LoadingState } from '@common/components';
import { FEATURE_FLAGS_QUERY } from '@common/graphql';
import { useQuery } from '@common/hooks';
import React, {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

// Add flag keys here like example below, sorted alphabetically
// FLAG_NAMES = ['flag_name_1', 'flag_name_2', ...];
const FLAG_NAMES: string[] = [];

const FLAG_DEFAULTS = buildFlagDefaults(FLAG_NAMES);

type FeatureFlags = typeof FLAG_DEFAULTS;

type SnakeToPascal<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToPascal<U>>}`
  : S;

type TransformedFlags = {
  [K in keyof FeatureFlags as `is${Capitalize<SnakeToPascal<K & string>>}Enabled`]: FeatureFlags[K];
};

const transformSnakeToPascalCase = (key: string) => {
  return key
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    .replace(/^[a-z]/, (letter) => letter.toUpperCase());
};

const transformFlagsToContext = (flags: FeatureFlags): TransformedFlags => {
  const camelFlags = {} as TransformedFlags;
  for (const key in flags) {
    if (!Object.prototype.hasOwnProperty.call(flags, key)) continue;
    const pascalCaseKey = transformSnakeToPascalCase(key);
    (camelFlags as Record<string, boolean>)[`is${pascalCaseKey}Enabled`] =
      flags[key as keyof FeatureFlags];
  }
  return camelFlags;
};

const FeatureFlagsContext = createContext<TransformedFlags>(
  transformFlagsToContext(FLAG_DEFAULTS)
);

export const FeatureFlagsProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const query = useQuery(FEATURE_FLAGS_QUERY, {
    variables: {
      flagNames: FLAG_NAMES,
    },
  });

  const effectiveFlags = useMemo(() => {
    const flags = {
      ...FLAG_DEFAULTS,
      ...((query.data?.featureFlags || {}) as FeatureFlags),
    };

    return transformFlagsToContext(flags);
  }, [query.data]);

  if (query.loading && !query.data) {
    return <LoadingState />;
  }

  return (
    <FeatureFlagsContext.Provider value={effectiveFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

// TransformedFlags will be:
// {
//   isFlagName1Enabled: false,
//   isFlagName2Enabled: false,
// }
export const useFeatureFlags = (): TransformedFlags =>
  useContext(FeatureFlagsContext);

function buildFlagDefaults(flagNames: string[]) {
  return flagNames.reduce(
    (acc, flagName) => {
      acc[flagName] = false;
      return acc;
    },
    {} as Record<string, boolean>
  );
}
